import React, { useState } from 'react';
import { createStyles } from './AssignmentSidebar.styles';
import {
  Box,
  Button,
  Divider,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import { ASSIGNMENTS } from '@app/constants/texts';
import { ResponsiveAssignment } from '@core/assignments/types';
import AssignmentList from '../List/AssignmentList';
import AssignmentTools from './AssignmentTools';
import CreateAssignmentModal from '../CreateAssignment/CreateAssignmentModal';

type Props = {
  assignments: ResponsiveAssignment[];
  backRoute: string | null;
  spaceId: string;
  baseDomain: string;
  isEducator?: boolean;
  isDesktop: boolean;
  onMaterialClick?: (materialId: string) => void;
};

const AssignmentSidebar = ({
  assignments,
  backRoute,
  spaceId,
  baseDomain,
  isEducator = false,
  isDesktop,
  onMaterialClick,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  // States for the assignment creation modal
  const [assignmentStates, setAssignmentStates] = useState({
    editingAssignment: null,
    isCreating: false,
    isCoupled: false,
  } as {
    editingAssignment: any;
    isCreating: boolean;
    isCoupled: boolean;
  });

  // All styles for the assignment tools component
  const assignmentToolStyles = {
    assignmentToolsWrapper: {
      ...styles.assignmentToolsWrapper,
      background: isDesktop
        ? theme?.palette?.background?.default
        : theme.palette.background.paper,
    },
    common: styles.common,
    avatarStyle: styles.avatarStyle,
    newTabIcon: styles.newTabIcon,
    toolTitle: styles.toolTitle,
    tooltipStyles: styles.tooltipStyles,
    modalContainer: styles.modalContainer,
    modalHeader: styles.modalHeader,
    modalContent: styles.modalContent,
    modalFooter: styles.modalFooter,
    modalCardTitle: styles.modalCardTitle,
    modalCardDescription: styles.modalCardDescription,
  };

  const handleClearEditModal = () => {
    setAssignmentStates((prev) => ({
      ...prev,
      editingAssignment: null,
    }));
  };

  const handleToggleCreateAssignment = () => {
    setAssignmentStates((prev) => ({
      ...prev,
      isCreating: !prev.isCreating,
    }));
    handleClearEditModal();
  };

  const { isCreating, isCoupled } = assignmentStates;

  return (
    <Box sx={styles.sidebarWrapper}>
      <Box sx={styles.buttonWrapper}>
        <NavigationBackHeader route={backRoute} />
      </Box>
      <Box sx={styles.sidebarContent}>
        <Box sx={styles.headerWrapper}>
          <Box sx={styles.headerIcon} aria-hidden>
            <Icons.AssessmentOutlined fontSize="medium" />
          </Box>
          <Box>
            <Typography component="h2" variant="h6" sx={styles.title}>
              {ASSIGNMENTS}
            </Typography>
          </Box>
        </Box>
        {isEducator && (
          <Box>
            <Button
              onClick={handleToggleCreateAssignment}
              type="button"
              kind="primary"
              sx={styles.createButton}
            >
              <Icons.Add />
              New assignment
            </Button>
          </Box>
        )}
        <Box>
          <AssignmentTools
            styles={assignmentToolStyles}
            baseDomain={baseDomain}
            spaceId={spaceId}
            isEducator={isEducator}
            isDesktop={isDesktop}
          />
        </Box>
        <Divider sx={{ my: '16px' }} />
        <Box sx={styles.listWrapper}>
          <AssignmentList
            assignments={assignments}
            spaceId={spaceId}
            isEducator={isEducator}
            onMaterialClick={onMaterialClick}
          />
        </Box>
        <CreateAssignmentModal
          key={`create-assignment-modal-${isCreating}`}
          isVisible={isCreating && !isCoupled}
          onClose={handleToggleCreateAssignment}
          editingAssignment={null}
          currentSpace={{ objectId: spaceId }}
          fetchAccessToken={async () => ''}
          fetchAssignments={async () => {}}
        />
      </Box>
    </Box>
  );
};

export default AssignmentSidebar;
